import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { compressImage } from "@/lib/compress-image";
import type {
  TraderBankRow,
  TraderDashboard,
  TraderDepositRow,
  TraderIbApplication,
  TraderKycSubmission,
  TraderWithdrawRow,
} from "@/types/trader";

/* ─── Dashboard ──────────────────────────────────────────────────────── */

export function useTraderDashboard() {
  return useQuery({
    queryKey: ["trader-dashboard"],
    queryFn: async () => unwrap<TraderDashboard>((await api.get("/user/dashboard")).data),
    staleTime: 15_000,
  });
}

/* ─── MT5 Account opening (self-serve) ──────────────────────────────── */

export function useTraderMt5Groups() {
  return useQuery({
    queryKey: ["trader-mt5-groups"],
    // Rows are curated group objects now — see useMt5GroupOptions (hooks/useMt5Manage.ts).
    queryFn: async () => unwrap<{ rows: Array<{ name: string; group: string; accountType: "demo" | "live"; leverageOptions: number[] }> }>((await api.get("/user/mt5/groups")).data),
    staleTime: 5 * 60_000,
  });
}

export interface OpenAccountInput {
  accountType: "live" | "demo";
  group: string;
  leverage: number;
  initialDeposit?: number;
}

export interface OpenAccountResult {
  login: number;
  password: string;
  investorPassword: string;
  group: string;
  accountType: "live" | "demo";
  leverage: number;
  balance: number;
  server: string;
}

export function useTraderOpenMt5Account() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OpenAccountInput) =>
      unwrap<OpenAccountResult>((await api.post("/user/mt5/accounts", input)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
    },
  });
}

export interface TraderMt5Account {
  id: number; mt5Login: string; server: string; type: string;
  accountType: string; leverage: number; groupName: string | null;
  balance: number; equity: number; status: string; openedAt: string;
}

export function useTraderMt5Accounts() {
  return useQuery({
    queryKey: ["trader-mt5-accounts"],
    queryFn: async () => unwrap<{ rows: TraderMt5Account[] }>((await api.get("/user/mt5/accounts")).data),
    staleTime: 30_000,
  });
}

export interface TraderTradeDeal {
  dealId: number;
  login: number;
  symbol: string | null;
  action: string;
  entryType: string | null;
  volume: number | null;
  priceOpen: number | null;
  priceClose: number | null;
  profit: number;
  commission: number;
  swap: number;
  comment: string | null;
  timeOpen: string | null;
  timeClose: string;
}

export interface TraderHistoryFilter {
  login?: number; symbol?: string; from?: string; to?: string;
  limit?: number; offset?: number;
}

export function useTraderTradeHistory(filter: TraderHistoryFilter = {}) {
  return useQuery({
    queryKey: ["trader-trade-history", filter],
    queryFn: async () => {
      const p = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v != null && v !== "") p.set(k, String(v)); });
      return unwrap<{ rows: TraderTradeDeal[]; total: number }>(
        (await api.get(`/user/mt5/trade-history?${p.toString()}`)).data,
      );
    },
    staleTime: 15_000,
  });
}

/* ─── Banks ──────────────────────────────────────────────────────────── */

export function useTraderBanks() {
  return useQuery({
    queryKey: ["trader-banks"],
    queryFn: async () => unwrap<{ rows: TraderBankRow[] }>((await api.get("/user/banks")).data),
    staleTime: 15_000,
  });
}

export interface BankInput {
  bankCountry: string;
  bankName: string;
  accountNumber: string;
  holderName: string;
  ifscSwift: string;
  bankAddress?: string;
}

export function useAddBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: BankInput) => {
      const { data } = await api.post("/user/banks", vars);
      return unwrap<{ id: number; status: string }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-banks"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
    },
  });
}

export function useDeleteBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/user/banks/${id}`);
      return unwrap<{ id: number; deleted: boolean }>(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trader-banks"] }),
  });
}

export function useSetPrimaryBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/user/banks/${id}/primary`);
      return unwrap<{ id: number; isPrimary: boolean }>(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trader-banks"] }),
  });
}

/* ─── Deposits ───────────────────────────────────────────────────────── */

export function useTraderDeposits() {
  return useQuery({
    queryKey: ["trader-deposits"],
    queryFn: async () => unwrap<{ rows: TraderDepositRow[] }>((await api.get("/user/deposits")).data),
    staleTime: 15_000,
  });
}

export function useSubmitDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      method: string;
      amount: number;
      currency: string;
      reference?: string;
      note?: string;
      proof?: File | null;
      bonusPackageId?: number | null;
      /** [workstream F] manual crypto rail: "trc20" | "bep20" */
      network?: string;
    }) => {
      const fd = new FormData();
      fd.append("method", vars.method);
      fd.append("amount", String(vars.amount));
      fd.append("currency", vars.currency);
      if (vars.reference) fd.append("reference", vars.reference);
      if (vars.note) fd.append("note", vars.note);
      if (vars.proof) fd.append("proof", await compressImage(vars.proof));
      if (vars.bonusPackageId) fd.append("bonusPackageId", String(vars.bonusPackageId));
      if (vars.network) fd.append("network", vars.network);
      const { data } = await api.post("/user/deposits", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return unwrap<{ id: number; status: string; hasProof: boolean; hosted?: boolean }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-deposits"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
    },
  });
}

/* ─── Withdrawals ────────────────────────────────────────────────────── */

export function useTraderWithdrawals() {
  return useQuery({
    queryKey: ["trader-withdrawals"],
    queryFn: async () =>
      unwrap<{ rows: TraderWithdrawRow[] }>((await api.get("/user/withdrawals")).data),
    staleTime: 15_000,
  });
}

export interface WithdrawInput {
  method: "confirmo" | "on_now" | "local_transfer" | "cash" | "bank" | "upi" | "crypto";
  amount: number;
  transactionPassword: string;
  /** [workstream F] email OTP from POST /user/withdrawals/send-otp */
  otp?: string;
  reason?: string;
  bankAccountId?: number;
  destinationAddress?: string;
  destinationNetwork?: string;
  note?: string;
}

export function useSubmitWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: WithdrawInput) => {
      const { data } = await api.post("/user/withdrawals", vars);
      return unwrap<{
        id: number;
        status: string;
        amount: string;
        heldAmount: string;
        walletBalance: string;
        walletHeld: string;
      }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-withdrawals"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
    },
  });
}

export function useCancelWithdrawal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post(`/user/withdrawals/${id}/cancel`);
      return unwrap<{ id: number; status: string }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-withdrawals"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
    },
  });
}

/* ─── KYC ────────────────────────────────────────────────────────────── */

export function useTraderKyc() {
  return useQuery({
    queryKey: ["trader-kyc"],
    queryFn: async () =>
      unwrap<{ rows: TraderKycSubmission[] }>((await api.get("/user/kyc")).data),
    staleTime: 15_000,
  });
}

export interface KycInput {
  idType: "passport" | "drivers_license" | "national_id";
  idNumber: string;
  fullName: string;
  dateOfBirth?: string;
  addressLine?: string;
  idFront: File;
  idBack?: File | null;
  selfie?: File | null;
  addressProof?: File | null;
}

export function useSubmitKyc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: KycInput) => {
      const fd = new FormData();
      fd.append("idType", vars.idType);
      fd.append("idNumber", vars.idNumber);
      fd.append("fullName", vars.fullName);
      if (vars.dateOfBirth) fd.append("dateOfBirth", vars.dateOfBirth);
      if (vars.addressLine) fd.append("addressLine", vars.addressLine);
      fd.append("idFront", await compressImage(vars.idFront));
      if (vars.idBack) fd.append("idBack", await compressImage(vars.idBack));
      if (vars.selfie) fd.append("selfie", await compressImage(vars.selfie));
      if (vars.addressProof) fd.append("addressProof", await compressImage(vars.addressProof));
      const { data } = await api.post("/user/kyc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return unwrap<{ id: number; status: string }>(data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trader-kyc"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

/* ─── IB Application ─────────────────────────────────────────────────── */

export function useTraderIbApplication() {
  return useQuery({
    queryKey: ["trader-ib-application"],
    queryFn: async () =>
      unwrap<{ rows: TraderIbApplication[] }>((await api.get("/user/ib-application")).data),
    staleTime: 15_000,
  });
}

export interface IbApplyInput {
  businessName: string;
  businessType: "sole_prop" | "company" | "partnership" | "individual";
  websiteUrl?: string;
  expectedMonthlyVolumeUsd?: number;
  marketingPlan?: string;
  declaredSponsorCode?: string;
  password?: string;
  questionnaire?: { numberOfClients: string; clientSource: string; avgDeposit: string; hearAbout: string };
}

export function useApplyForIb() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: IbApplyInput) => {
      const { data } = await api.post("/user/ib-application", vars);
      return unwrap<{ id: number; status: string }>(data);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trader-ib-application"] }),
  });
}

/* ─── Profile (self-service) ────────────────────────────────────────── */

export interface UserProfile {
  id: number;
  email: string;
  fullName: string;
  phone: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  role: string;
  status: string;
  kycStatus: string;
  twoFactorEnabled: boolean;
  avatarUrl: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UserSession {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  lastSeenAt: string;
  createdAt: string;
  expiresAt: string;
}

export function useUserProfile() {
  return useQuery({
    queryKey: ["user-profile"],
    queryFn: async () => unwrap<UserProfile>((await api.get("/user/profile")).data),
    staleTime: 60_000,
  });
}

export function useUpdateUserProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<UserProfile>) => unwrap<Partial<UserProfile>>((await api.patch("/user/profile", patch)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-profile"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) =>
      unwrap<{ changed: boolean }>((await api.post("/user/profile/password", body)).data),
  });
}

export function useUserSessions() {
  return useQuery({
    queryKey: ["user-sessions"],
    queryFn: async () => unwrap<{ rows: UserSession[] }>((await api.get("/user/profile/sessions")).data),
    staleTime: 30_000,
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => unwrap<{ revoked: boolean }>((await api.post(`/user/profile/sessions/${id}/revoke`)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-sessions"] }),
  });
}

/* ─── Fund Transfers ────────────────────────────────────────────────── */

export interface TransferRow {
  id: number;
  amountUsd: string;
  tradingAccountId: number | null;
  status: string;
  note: string | null;
  declineReason: string | null;
  createdAt: string;
  completedAt: string | null;
}

export function useUserTransfers() {
  return useQuery({
    queryKey: ["user-transfers"],
    queryFn: async () => unwrap<{ rows: TransferRow[] }>((await api.get("/user/transfers")).data),
    staleTime: 30_000,
  });
}

export function useCreateInternalTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { amountUsd: number; tradingAccountId: number; note?: string }) =>
      unwrap<TransferRow>((await api.post("/user/transfers/internal", body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-transfers"] }),
  });
}

export function useWalletToAccountTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { amountUsd: number; tradingAccountId: number; note?: string }) =>
      unwrap<TransferRow & { mt5Balance: number }>(
        (await api.post("/user/transfers/wallet-to-account", body)).data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-transfers"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
      qc.invalidateQueries({ queryKey: ["trader-mt5-accounts"] });
    },
  });
}

export function useAccountToWalletTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { amountUsd: number; tradingAccountId: number; note?: string }) =>
      unwrap<TransferRow & { mt5Balance: number }>(
        (await api.post("/user/transfers/account-to-wallet", body)).data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-transfers"] });
      qc.invalidateQueries({ queryKey: ["trader-dashboard"] });
      qc.invalidateQueries({ queryKey: ["trader-mt5-accounts"] });
    },
  });
}

export function useCreateExternalTransfer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { amountUsd: number; recipientEmail: string; note?: string }) =>
      unwrap<TransferRow>((await api.post("/user/transfers/external", body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-transfers"] }),
  });
}

/* ─── Bonus & Loyalty ───────────────────────────────────────────────── */

export interface BonusGrantRow {
  id: number;
  amountUsd: string;
  sourceType: string | null;
  status: string;
  grantedAt: string;
  expiresAt: string | null;
  turnoverRequiredUsd: string | null;
  turnoverProgressUsd: string;
  notes: string | null;
}

export interface BonusPackageRow {
  id: number;
  name: string;
  description?: string | null;
}

export function useUserBonusGrants() {
  return useQuery({
    queryKey: ["user-bonus-grants"],
    queryFn: async () =>
      unwrap<{
        rows: BonusGrantRow[];
        totals: { totalGrantedUsd: string; loyaltyPointsUsd: string; activeCount: number };
      }>((await api.get("/user/bonus/grants")).data),
    staleTime: 60_000,
  });
}

export function useUserBonusPackages() {
  return useQuery({
    queryKey: ["user-bonus-packages"],
    queryFn: async () => unwrap<{ rows: BonusPackageRow[] }>((await api.get("/user/bonus/packages")).data),
    staleTime: 5 * 60_000,
  });
}

/* ─── Support tickets ───────────────────────────────────────────────── */

export interface TicketRow {
  id: number;
  subject: string;
  category: string | null;
  priority: string;
  status: string;
  lastMessageAt: string;
  createdAt: string;
}

export interface TicketMessage {
  id: number;
  authorKind: string;
  body: string;
  createdAt: string;
}

export function useUserTickets() {
  return useQuery({
    queryKey: ["user-tickets"],
    queryFn: async () => unwrap<{ rows: TicketRow[] }>((await api.get("/user/support/tickets")).data),
    staleTime: 30_000,
  });
}

export function useUserTicket(id: number | null) {
  return useQuery({
    queryKey: ["user-ticket", id],
    queryFn: async () => unwrap<{ ticket: TicketRow; messages: TicketMessage[] }>((await api.get(`/user/support/tickets/${id}`)).data),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { subject: string; body: string; category?: string; priority?: string }) =>
      unwrap<TicketRow>((await api.post("/user/support/tickets", body)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-tickets"] }),
  });
}

export function useReplyToTicket(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: string) =>
      unwrap<TicketMessage>((await api.post(`/user/support/tickets/${id}/messages`, { body })).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-ticket", id] });
      qc.invalidateQueries({ queryKey: ["user-tickets"] });
    },
  });
}

/* ─── IB portal ─────────────────────────────────────────────────────── */

export interface IbProfile {
  userId: number;
  tenantId: number;
  sponsorId: number | null;
  treeDepth: number;
  referralCode: string;
  referralUrl: string | null;
  isMasterIb: boolean;
  groupSlug: string;
  businessName: string | null;
  totalClients: number;
  totalCommissionEarnedUsd: string;
  totalCommissionPaidUsd: string;
  kycStatus: string;
}

export interface IbDashboardStats {
  pendingUsd: string;
  approvedUsd: string;
  paidUsd: string;
  totalUsd: string;
  eventCount: number;
}

export interface IbClient {
  id: number;
  fullName: string;
  email: string;
  country: string | null;
  kycStatus: string;
  status: string;
  createdAt: string;
  referrerCode: string | null;
}

export interface IbCommissionRow {
  id: number;
  sourceType: string;
  sourceAmountUsd: string;
  ibLevel: number;
  ratePercent: string | null;
  amountUsd: string;
  status: string;
  paidAt: string | null;
  createdAt: string;
  traderUserId: number;
  traderName: string | null;
}

export function useIbMe() {
  return useQuery({
    queryKey: ["ib-me"],
    queryFn: async () => unwrap<IbProfile>((await api.get("/user/ib-portal/me")).data),
    staleTime: 60_000,
    retry: false,
  });
}

export function useIbDashboard() {
  return useQuery({
    queryKey: ["ib-dashboard"],
    queryFn: async () => unwrap<{ profile: IbProfile; stats: IbDashboardStats }>((await api.get("/user/ib-portal/dashboard")).data),
    staleTime: 30_000,
    retry: false,
  });
}

export function useIbClients() {
  return useQuery({
    queryKey: ["ib-clients"],
    queryFn: async () => unwrap<{ rows: IbClient[] }>((await api.get("/user/ib-portal/clients")).data),
    staleTime: 60_000,
    retry: false,
  });
}

export function useIbCommissions() {
  return useQuery({
    queryKey: ["ib-commissions"],
    queryFn: async () => unwrap<{ rows: IbCommissionRow[] }>((await api.get("/user/ib-portal/commissions")).data),
    staleTime: 60_000,
    retry: false,
  });
}

export function useIbWithdraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { amountUsd: number; bankAccountId: number }) =>
      unwrap<{ id: number; amount: string; status: string }>((await api.post("/user/ib-portal/withdraw", body)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ib-dashboard"] });
      qc.invalidateQueries({ queryKey: ["ib-commissions"] });
    },
  });
}

/* ─── Payment methods (admin-enabled rails) ──────────────────────────── */

export type PaymentMethodKey = "bank" | "local_transfer" | "confirmo" | "on_now" | "cash" | "upi" | "crypto" | "fryptopay";

export interface EnabledMethod {
  method: PaymentMethodKey;
  label: string;
  instructions: string | null;
  /** [workstream F] secret-free rail details (UPI/QR, crypto networks, bank card,
   * hosted checkout flag, FryptoPay configured flag, loop-policy cap). */
  details?: {
    upiId?: string | null; payeeName?: string | null; qrUrl?: string | null;
    networks?: { key: string; label: string; address: string; qrUrl: string | null }[];
    bank?: { bankName?: string; accountName?: string; accountNumber?: string; ifscSwift?: string; branch?: string } | null;
    hosted?: boolean;
    configured?: boolean;
  };
  /** Loop Policy: the most this trader may withdraw via this rail. */
  maxAmount?: number;
}

export interface CompanyBankInfo {
  id: number;
  bankName: string;
  accountName: string;
  accountNumber: string;
  ifscSwift: string;
  bankCountry: string;
  bankAddress: string | null;
  currency: string;
  notes: string | null;
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: ["payment-methods"],
    queryFn: async () =>
      unwrap<{ deposit: EnabledMethod[]; withdraw: EnabledMethod[]; companyBanks: CompanyBankInfo[]; loopPolicy?: boolean }>(
        (await api.get("/user/payment-methods")).data,
      ),
    staleTime: 60_000,
  });
}
